/**
 * Configuration multi-serveurs du panel Recube.
 *
 * Le panel est mutualisé : aujourd'hui Paladium (Bedrock), demain NationsGlory (Java).
 * Chaque jeu partage les fonctionnalités communes et peut en activer de spécifiques,
 * et pointe vers son propre backend (IP différente : .26 = Paladium, .27 = NationsGlory).
 */

/** Fonctionnalités activables par jeu (une clé par page/module du panel). */
export type FeatureKey =
  | 'dashboard'
  | 'staff'
  | 'players'
  | 'sanctions'
  | 'console'
  | 'stats'
  | 'logs'
  | 'banwords'
  | 'whitelist'
  | 'ranks';

export interface GameConfig {
  /** Identifiant technique stable (utilisé pour la persistance + le routage backend). */
  id: string;
  /** Nom affiché dans le sélecteur. */
  name: string;
  /** Nom court / trigramme éventuel. */
  shortName: string;
  /**
   * Base URL de l'API de ce jeu. Vide = chemin relatif `/api` (proxifié vers le backend .26).
   * Configurable par variable d'env pour chaque environnement.
   */
  apiBaseUrl: string;
  /** Couleur d'accent du jeu (surcharge le violet Recube dans le panel). */
  accent: {
    primary: string;
    hover: string;
    light: string;
  };
  /** Fonctionnalités disponibles pour ce jeu. */
  features: FeatureKey[];
}

/** Ensemble des fonctionnalités communes (présentes sur tous les jeux aujourd'hui). */
const COMMON_FEATURES: FeatureKey[] = [
  'dashboard',
  'staff',
  'players',
  'sanctions',
  'console',
  'stats',
  'logs',
  'banwords',
  'whitelist',
  'ranks',
];

export const GAMES: GameConfig[] = [
  {
    id: 'paladium',
    name: 'Paladium',
    shortName: 'PALA',
    // Backend historique (.26) — chemin relatif proxifié comme aujourd'hui.
    apiBaseUrl: import.meta.env.VITE_API_URL_PALADIUM ?? '',
    accent: {
      primary: '#FF5C00',
      hover: '#E0621B',
      light: 'rgba(255, 92, 0, 0.15)',
    },
    features: [...COMMON_FEATURES],
  },
  {
    id: 'nationsglory',
    name: 'NationsGlory',
    shortName: 'NG',
    // Backend NationsGlory (.27) — à câbler côté proxy/infra (placeholder pour l'instant).
    apiBaseUrl: import.meta.env.VITE_API_URL_NG ?? '/api-ng',
    accent: {
      primary: '#2563EB',
      hover: '#1D4ED8',
      light: 'rgba(37, 99, 235, 0.15)',
    },
    features: [...COMMON_FEATURES],
  },
  {
    id: 'recube',
    name: 'Recube',
    shortName: 'RC',
    apiBaseUrl: import.meta.env.VITE_API_URL_RECUBE ?? '/api-recube',
    accent: {
      primary: '#8B3FF1',
      hover: '#7A2FE0',
      light: 'rgba(139, 63, 241, 0.15)',
    },
    features: [...COMMON_FEATURES],
  },
];

export const DEFAULT_GAME_ID = GAMES[0]!.id;

export function getGameById(id: string): GameConfig {
  return GAMES.find((g) => g.id === id) ?? GAMES[0]!;
}

/**
 * Un groupe appartient-il au projet donné ? Tolérant : un groupe sans `projects`
 * (backend pas encore à jour, ou données historiques) est rattaché au projet par
 * défaut (paladium) plutôt que masqué partout.
 */
export function projectMatches(projects: string[] | undefined | null, gameId: string): boolean {
  const p = projects ?? [];
  return p.length > 0 ? p.includes(gameId) : gameId === DEFAULT_GAME_ID;
}
